import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatApproverChoice {
  id: string;
  email: string;
  forename?: string;
  surname?: string;
}

export interface ChatApproverAnswer {
  approverId: string;
  approverName: string;
  ruleId: number;
  level: number;
}

/**
 * Renders the approver picker widget inside the chat widget — a plain
 * dropdown of the single outstanding rule/level's available approvers (see
 * select_approver's action="get"/"select" nextApprovalStep). Only ever shown
 * for the Manual Select Approver / Multi-Level Manual Approval configs —
 * Pre-selected Approver and Profile Approver need no user interaction and
 * never reach the chat widget. Like chat-payment-input this isn't
 * per-passenger and has no Skip — an outstanding approval level is always
 * mandatory before booking. A Multi-Level Manual Approval basket renders a
 * fresh instance of this widget for each level in turn, once the previous
 * one has been confirmed.
 */
@Component({
  selector: 'app-chat-approver-input',
  standalone: false,
  templateUrl: './chat-approver-input.component.html',
  styleUrls: ['./chat-approver-input.component.scss']
})
export class ChatApproverInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() choices: ChatApproverChoice[] = [];
  @Input() ruleId = 0;
  @Input() level = 0;
  /** Total levels for this rule (see ApproverSelectionPrompt.totalLevels) —
   * >1 means Multi-Level Manual Approval, so the widget shows "Select
   * Approver for level N of totalLevels" the same way the old approvers
   * modal (approvers-modal.component.html) labels each level, instead of
   * just "Approver" as for the single-level Manual Select Approver config. */
  @Input() totalLevels = 1;
  @Output() answer = new EventEmitter<ChatApproverAnswer>();

  /** Bound via [(ngModel)] on the <select> — holds the approver id, or ''
   * when nothing is chosen yet. */
  selectedId = '';
  /** Human-readable summary of what was submitted, shown in place of the
   * control once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.selectedId = '';
    this.chosenSummary = null;
  }

  get isValid(): boolean {
    return !!this.selectedId;
  }

  /** Mirrors approvers-modal.component.html's "Select Approver for level N"
   * label; only appended with "of totalLevels" for Multi-Level Manual
   * Approval (totalLevels > 1) so the single-level Manual Select Approver
   * flow keeps its plain "Approver" label. */
  get fieldLabel(): string {
    if (this.totalLevels > 1) {
      const level = this.level;
      const totalLevels = this.totalLevels;
      return $localize`:@@chatApproverFieldLabelLevel:Select Approver for level ${level}:level: of ${totalLevels}:totalLevels:`;
    }
    return $localize`:@@chatApproverLabel:Approver`;
  }

  approverName(choice: ChatApproverChoice): string {
    const name = [choice.forename, choice.surname].filter(Boolean).join(' ');
    return name || choice.email;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const choice = this.choices.find((c) => c.id === this.selectedId);
    if (!choice) {
      return;
    }
    this.chosenSummary = this.approverName(choice);
    this.answer.emit({
      approverId: choice.id,
      approverName: this.chosenSummary,
      ruleId: this.ruleId,
      level: this.level
    });
  }
}
